import React, { useCallback, useEffect, useState } from "react";
import buildApiUrl from "../services/apiHelpers";
import {
  Eye,
  Edit2,
  Trash2,
  Download,
  AlertCircle,
  RefreshCw,
  Filter,
  Loader,
  X,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

// ─── PREMIUM COMPACT STACKED LIGHT THEME CSS ─────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --surface-base: #ffffff;
    --surface-raised: #f8fafc;
    --border-subtle: #e2e8f0;
    --border-strong: #cbd5e1;
    
    --text-main: #0f172a;
    --text-muted: #334155;
    --text-dim: #64748b;
    
    --brand-accent: #2563eb;
    --brand-surface: #eff6ff;

    --semantic-success: #056f43;
    --semantic-danger: #e11d48;
    
    --shadow-sm: 0 1px 3px rgba(15, 23, 42, 0.05);
    --shadow-md: 0 4px 12px -2px rgba(15, 23, 42, 0.05);
    
    --radius-pill: 9999px;
    --radius-interactive: 8px;
    --radius-container: 12px;
  }

  body {
    font-family: 'Plus Jakarta Sans', sans-serif;
    color: var(--text-main);
    -webkit-font-smoothing: antialiased;
  }

  .appt-root {
    font-family: 'Plus Jakarta Sans', sans-serif;
    min-height: 100vh;
    background: transparent !important;
  }

  .appt-container {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    padding: 16px 0 32px 0;
  }

  /* ─── Transparent Header Layout ─── */
  .appt-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    gap: 16px;
    background: transparent !important;
    border: none !important;
    padding: 12px 4px;
    box-shadow: none !important;
    border-radius: 0;
  }

  .appt-header-left h1 {
    font-size: 26px;
    font-weight: 800;
    color: var(--text-main);
    letter-spacing: -0.5px;
  }

  .appt-header-left p {
    font-size: 13px;
    color: var(--text-dim);
    margin-top: 4px;
  }

  .appt-header-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  /* ─── Premium Colored Action Controls ─── */
  .appt-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 18px;
    border-radius: var(--radius-interactive);
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.15s ease;
    white-space: nowrap;
  }

  .appt-btn-primary {
    background: linear-gradient(135deg, var(--brand-accent) 0%, #1d4ed8 100%);
    color: var(--surface-base);
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
    border: 1px solid #1d4ed8;
  }

  .appt-btn-primary:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(37, 99, 235, 0.3);
  }

  .appt-btn-secondary {
    background: var(--surface-base);
    color: var(--text-muted);
    border: 1.5px solid var(--border-strong);
    box-shadow: var(--shadow-sm);
  }

  .appt-btn-secondary:hover {
    background: var(--surface-raised);
    border-color: var(--text-muted);
    color: var(--text-main);
  }

  /* ─── Stats Grid ─── */
  .appt-stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
  }

  .appt-stat-card {
    background: var(--surface-base);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-container);
    padding: 16px 20px;
    box-shadow: var(--shadow-sm);
    position: relative;
    overflow: hidden;
  }

  .appt-stat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; bottom: 0; width: 4px;
  }
  .appt-stat-card.vol::before { background: #2563eb; }
  .appt-stat-card.bk::before { background: #0ea5e9; }
  .appt-stat-card.res::before { background: #10b981; }
  .appt-stat-card.nos::before { background: #f43f5e; }

  .appt-stat-label {
    font-size: 11px;
    font-weight: 700;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .appt-stat-value {
    font-size: 26px;
    font-weight: 800;
    color: var(--text-main);
    letter-spacing: -0.5px;
  }

  .appt-stat-subtext {
    font-size: 12px;
    color: var(--text-dim);
    margin-top: 2px;
  }

  /* ─── Filters ─── */
  .appt-filters {
    background: var(--surface-base);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-container);
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: var(--shadow-sm);
  }

  .appt-filters-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--text-main);
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .appt-mode-toggle {
    display: flex;
    justify-content: center;
    background: #e2e8f0;
    padding: 3px;
    border-radius: 8px;
    margin-bottom: 16px;
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
  }

  .appt-mode-btn {
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    border: none;
    background: transparent;
    cursor: pointer;
    color: var(--text-muted);
  }

  .appt-mode-btn.active {
    background: var(--surface-base);
    color: var(--brand-accent);
    box-shadow: var(--shadow-sm);
  }

  .appt-date-inputs, .appt-filters-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 12px;
  }
  .appt-filters-row { margin-bottom: 0; }

  .appt-input-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .appt-input-group label {
    font-size: 11px;
    font-weight: 700;
    color: var(--text-muted);
    text-transform: uppercase;
  }

  .appt-input {
    padding: 10px 12px;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-interactive);
    font-size: 13px;
    color: var(--text-main);
    background: var(--surface-raised);
    width: 100%;
    font-family: inherit;
  }

  .appt-input:focus {
    outline: none;
    border-color: var(--brand-accent);
    box-shadow: 0 0 0 3px var(--brand-glow);
  }

  select.appt-input {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 32px;
  }

  /* ─── Tabs ─── */
  .appt-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 16px;
    overflow-x: auto;
    padding: 4px;
    background: #e2e8f0;
    border-radius: 10px;
    width: fit-content;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
  }

  .appt-tabs::-webkit-scrollbar { display: none; }

  .appt-tab {
    padding: 8px 14px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    color: var(--text-muted);
    white-space: nowrap;
  }

  .appt-tab.active {
    background: var(--brand-accent);
    color: var(--surface-base);
    box-shadow: var(--shadow-sm);
  }

  /* ─── Desktop Table Ledger ─── */
  .appt-table-wrapper {
    background: var(--surface-base);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-container);
    overflow: hidden;
    box-shadow: var(--shadow-sm);
  }

  /* ─── Card List Ledger (alternative to table) ─── */
  .appt-card-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .appt-card-group {
    background: var(--surface-base);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-container);
    padding: 12px;
    box-shadow: var(--shadow-sm);
  }

  .appt-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--border-subtle);
  }

  .appt-card-group-title { font-weight: 800; color: var(--text-main); }
  .appt-card-group-sub { font-size: 13px; color: var(--text-dim); }

  .appt-card-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px dashed var(--border-subtle);
    background: var(--surface-base);
  }

  .appt-card-row:last-child { border-bottom: none; }

  /* Alternating white / light grey rows to mimic table striping */
  .appt-card-row:nth-child(odd) { background: #ffffff; }
  .appt-card-row:nth-child(even) { background: #f8fafc; }

  .appt-card-left { display: flex; gap: 12px; align-items: center; flex: 1; }
  .appt-card-left .appt-avatar { width: 40px; height: 40px; font-size: 13px; }
  .appt-card-meta { display: flex; flex-direction: column; gap: 2px; }
  .appt-patient-name { font-weight: 700; }
  .appt-patient-phone { color: var(--text-dim); font-size: 13px; }

  .appt-card-right { display: flex; gap: 8px; align-items: center; }
  .appt-card-right .appt-actions { gap: 6px; }


  .appt-table {
    width: 100%;
    border-collapse: collapse;
  }

  .appt-table th {
    background: linear-gradient(135deg, var(--brand-accent) 0%, #1d4ed8 100%);
    color: #ffffff;
    padding: 12px 16px;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #1e40af;
    text-align: left;
  }

  .appt-table td {
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-subtle);
    font-size: 13px;
    color: var(--text-main);
    vertical-align: middle;
  }

  .appt-table tbody tr { background: var(--surface-base); }
  .appt-table tbody tr:nth-child(even) { background: #f8fafc; }
  .appt-table tbody tr:hover { background: #f1f5f9; }
  .appt-table tbody tr.appt-history-row { background: #fdfdfd; opacity: 0.85; }

  /* ─── Status Badges ─── */
  .appt-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .appt-badge.booked { background: #e0f2fe; color: #0369a1; }
  .appt-badge.arrived { background: #fef3c7; color: #b45309; }
  .appt-badge.completed { background: #dcfce7; color: #15803d; }
  .appt-badge.cancelled { background: #ffe4e6; color: #b91c1c; }
  .appt-badge.no_show { background: #f1f5f9; color: #475569; }
  .appt-badge.consulting { background: #f3e8ff; color: #6b21a8; }

  .appt-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--brand-surface);
    color: var(--brand-accent);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 11px;
    border: 1px solid #bfdbfe;
  }

  .appt-actions { display: flex; gap: 4px; justify-content: flex-end; }

  .appt-icon-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid var(--border-strong);
    background: var(--surface-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-muted);
  }

  .appt-icon-btn:hover {
    background: var(--text-main);
    color: var(--surface-base);
    border-color: transparent;
  }
  .appt-icon-btn.danger:hover { background: var(--semantic-danger); color: var(--surface-base); }

  /* ─── Empty Placeholder ─── */
  .appt-empty-container {
    background: var(--surface-base);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-container);
    padding: 48px 16px;
    text-align: center;
    box-shadow: var(--shadow-sm);
  }

  .appt-empty-headline { font-size: 16px; font-weight: 700; color: var(--text-main); margin-bottom: 4px; }
  .appt-empty-subline { font-size: 13px; color: var(--text-dim); }

  /* ─── Modals ─── */
  .appt-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
    backdrop-filter: blur(4px);
  }

  .appt-modal {
    background: var(--surface-base);
    border-radius: var(--radius-container);
    max-width: 420px;
    width: 100%;
    box-shadow: var(--shadow-blur-lg);
    border: 1px solid var(--border-subtle);
    overflow: hidden;
  }

  .appt-modal-header { padding: 16px 20px; border-bottom: 1px solid var(--border-subtle); display: flex; justify-content: space-between; align-items: center; background: var(--surface-raised); }
  .appt-modal-title { font-size: 15px; font-weight: 700; color: var(--text-main); }
  .appt-modal-close { background: transparent; border: none; cursor: pointer; color: var(--text-dim); display: flex; }
  .appt-modal-body { padding: 20px; }
  .appt-modal-field { margin-bottom: 12px; }
  .appt-modal-label { font-size: 10px; font-weight: 700; color: var(--text-dim); text-transform: uppercase; margin-bottom: 4px; }
  .appt-modal-value { font-size: 13px; color: var(--text-main); font-weight: 600; padding: 10px 12px; background: var(--surface-raised); border-radius: var(--radius-interactive); border: 1px solid var(--border-subtle); }
  .appt-modal-footer { padding: 14px 20px; border-top: 1px solid var(--border-subtle); display: flex; gap: 8px; justify-content: flex-end; background: var(--surface-raised); }

  .appt-history-badge { background: #cbd5e1; color: #1e293b; padding: 1px 4px; border-radius: 3px; font-size: 9px; font-weight: 700; text-transform: uppercase; }

  /* ─── RESPONSIVE BREAKPOINTS & MOBILE STACKED VIEW ─── */
  @media (max-width: 1024px) {
    .appt-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
  }

  @media (max-width: 480px) {
    .appt-container { padding: 12px 0; }
    .appt-header { padding: 12px 4px; flex-direction: column; align-items: stretch; gap: 12px; }
    .appt-header-actions { width: 100%; }
    .appt-header-actions .appt-btn { flex: 1; }
    .appt-date-inputs, .appt-filters-row { grid-template-columns: 1fr; gap: 12px; }
    .appt-filters { padding: 16px; }
    
    .appt-table-wrapper { background: transparent; box-shadow: none; border: none; overflow: visible; }
    .appt-table thead { display: none; }
    .appt-table, .appt-table tbody, .appt-table tr, .appt-table td { display: block; width: 100%; }
    
    .appt-table tr { 
      background: var(--surface-base);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius-container);
      padding: 12px;
      box-shadow: var(--shadow-sm);
      margin-bottom: 12px;
    }
    .appt-table td { 
      padding: 6px 4px; 
      display: flex; 
      justify-content: space-between; 
      align-items: center; 
      border-bottom: 1px dashed var(--border-subtle); 
    }
    .appt-table td:last-child { border-bottom: none; padding-bottom: 0; }
    .appt-table td::before { content: attr(data-label); font-weight: 700; color: var(--text-dim); font-size: 13px; }
    .appt-table td .appt-actions { justify-content: flex-end; }
  }

  @media (max-width: 480px) {
    .appt-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
    .appt-stat-card { padding: 12px; }
    .appt-stat-value { font-size: 22px; }
    .appt-modal-footer { flex-direction: column; }
    .appt-modal-footer .appt-btn { width: 100% !important; }
  }

  /* ─── PROFESSIONAL MOBILE CARD LAYOUT ─── */
  @media (max-width: 768px) {
    .appt-table-wrapper { padding: 8px; }
    .appt-card-list { display: flex; flex-direction: column; gap: 10px; }
    
    .appt-card-group { padding: 0; background: transparent; margin-bottom: 0; }
    .appt-card-header { 
      display: flex; 
      justify-content: space-between; 
      align-items: center; 
      padding: 0 4px 8px 4px; 
      border-bottom: 1px solid var(--border-subtle);
      margin-bottom: 8px;
    }
    .appt-card-group-title { font-size: 13px; font-weight: 700; color: var(--text-main); }
    .appt-card-group-sub { font-size: 12px; color: var(--text-dim); margin-top: 2px; }
    
    .appt-card-row { 
      background: var(--surface-base);
      border: 1px solid var(--border-subtle);
      border-radius: 10px;
      padding: 10px;
      box-shadow: var(--shadow-sm);
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
    }
    
    .appt-card-left { 
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
      width: 100%;
    }
    .appt-avatar { 
      width: 40px; 
      height: 40px; 
      border-radius: 50%; 
      background: var(--brand-surface); 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      color: var(--brand-accent); 
      font-weight: 700;
      font-size: 16px;
      flex-shrink: 0;
    }
    .appt-card-meta { 
      overflow: hidden; 
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;
      width: 100%;
    }
    .appt-patient-name { 
      font-weight: 700; 
      font-size: 13px; 
      white-space: nowrap; 
      overflow: hidden; 
      text-overflow: ellipsis; 
      color: var(--text-main);
    }
    .appt-patient-phone { 
      font-size: 11px; 
      color: var(--text-dim);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .appt-card-details,
    .appt-card-meta-actions {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }
    .appt-card-details {
      flex-direction: column;
      align-items: stretch;
    }
    .appt-card-doctor-name {
      font-weight: 700;
      font-size: 13px;
      color: var(--text-main);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .appt-card-datetime {
      font-size: 12px;
      color: var(--text-dim);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .appt-card-meta-actions {
      justify-content: space-between;
      align-items: center;
    }
    .appt-card-meta-actions .appt-actions {
      justify-content: flex-start;
      flex-wrap: wrap;
    }
    .appt-card-meta-actions .appt-icon-btn {
      min-width: 40px;
    }
    
    .appt-badge {
      font-size: 10px;
      font-weight: 700;
      padding: 2px 6px;
      border-radius: 4px;
      text-transform: uppercase;
      text-align: center;
      white-space: nowrap;
    }
    
    .appt-actions { 
      display: flex; 
      flex-direction: column;
      gap: 4px; 
      align-items: center;
    }
    .appt-icon-btn { 
      width: 28px; 
      height: 28px; 
      border-radius: 6px; 
      display: inline-flex; 
      align-items: center; 
      justify-content: center; 
      background: transparent; 
      border: 1px solid var(--border-subtle);
      cursor: pointer;
      transition: all 0.2s ease;
      color: var(--text-muted);
      padding: 0;
    }
    .appt-icon-btn:hover {
      background: var(--brand-surface);
      color: var(--brand-accent);
      border-color: var(--brand-accent);
    }
    .appt-icon-btn.danger {
      color: var(--semantic-danger);
    }
    .appt-icon-btn.danger:hover {
      background: #fee2e2;
      border-color: var(--semantic-danger);
    }
  }
`;

const getTodayIST = () => new Date().toISOString().split("T")[0];
const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

// API base is provided by services/apiConfig
function AppointmentsPremium() {
  if (!document.getElementById("appt-styles")) {
    const el = document.createElement("style");
    el.id = "appt-styles";
    el.textContent = CSS;
    document.head.appendChild(el);
  }

  const { auth } = useAuth();
  const clinicId = Number(auth?.clinic?.id) || null;

  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(!clinicId);
  const [error, setError] = useState(clinicId ? null : "Clinic ID not found. Please login again.");
  const [lastRefreshAt, setLastRefreshAt] = useState(Date.now());

  const [dateMode, setDateMode] = useState("future");
  const [particularDate, setParticularDate] = useState(getTodayIST());
  const [startDate, setStartDate] = useState(getTodayIST());
  const [endDate, setEndDate] = useState(addDays(getTodayIST(), 30));
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [tabValue, setTabValue] = useState("all");
  const [searchPatient, setSearchPatient] = useState("");

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const [rescheduleDate, setRescheduleDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [fetchingSlots, setFetchingSlots] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    function onResize() { setWindowWidth(window.innerWidth); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  

  const fetchDoctors = useCallback(async () => {
    if (!clinicId) return;
    try {
      const url = buildApiUrl(`doctors/by-clinic-id/${clinicId}`);
      console.log("Fetching doctors from:", url);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      setDoctors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  }, [clinicId]);

  const fetchAppointments = useCallback(async () => {
    if (!clinicId) return;
    try {
      setLoading(true);
      setError(null);

      let start = startDate;
      let end = endDate;

      if (dateMode === "future") {
        start = getTodayIST();
        end = addDays(getTodayIST(), 90);
      } else if (dateMode === "particular") {
        start = particularDate;
        end = particularDate;
      }

      let url = buildApiUrl(`appointments/by-clinic/${clinicId}?startDate=${start}&endDate=${end}`);
      if (selectedDoctor) url += `&doctorId=${selectedDoctor}`;
      console.log("Fetching appointments from:", url);
      const res = await fetch(url, {
        credentials: "include",
        headers: { "X-Clinic-Id": String(clinicId) },
      });

      if (res.status === 403) {
        throw new Error("Access denied. Please check your subscription or login again.");
      }
      if (res.status === 404) {
        throw new Error("Clinic not found");
      }
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.message || `HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      console.log("Appointments fetched:", data);
      setAppointments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [clinicId, dateMode, particularDate, startDate, endDate, selectedDoctor]);

  useEffect(() => {
    if (clinicId) {
      fetchDoctors();
      fetchAppointments();
    }
  }, [clinicId, fetchDoctors, fetchAppointments]);

  useEffect(() => {
    if (!clinicId) return;
    const intervalId = window.setInterval(() => {
      fetchAppointments();
      setLastRefreshAt(Date.now());
    }, 20000);

    return () => window.clearInterval(intervalId);
  }, [clinicId, fetchAppointments]);

  useEffect(() => {
    if (openRescheduleModal && selectedAppointment && rescheduleDate) {
      const doctorIdForSlots = selectedAppointment?.doctor_id || selectedAppointment?.doctorId;
      if (!doctorIdForSlots) return;
      setFetchingSlots(true);
      const slotsUrl = buildApiUrl(`slots?doctorId=${doctorIdForSlots}&date=${rescheduleDate}`);
      console.log("Fetching slots from:", slotsUrl);
      fetch(slotsUrl)
        .then((r) => r.json())
        .then((data) => {
          if (data.slots && Array.isArray(data.slots)) {
            setSlots(data.slots.map((s) => s.slotTime || s.slot));
            setBookedSlots(data.slots.filter((s) => s.isBooked || s.booked).map((s) => s.slotTime || s.slot));
          }
          setSelectedSlot("");
        })
        .catch((e) => console.error("Error fetching slots:", e))
        .finally(() => setFetchingSlots(false));
    }
  }, [rescheduleDate, openRescheduleModal, selectedAppointment]);

  const filteredAppointments = React.useMemo(() => {
    let filtered = appointments;
    if (tabValue !== "all") filtered = filtered.filter((a) => a.status === tabValue);
    if (searchPatient) {
      filtered = filtered.filter(
        (a) =>
          a.patient_name?.toLowerCase().includes(searchPatient.toLowerCase()) ||
          a.phone?.includes(searchPatient)
      );
    }
    return filtered;
  }, [appointments, tabValue, searchPatient]);

  const groupedAppointments = React.useMemo(() => {
    const map = {};
    filteredAppointments.forEach((a) => {
      const doctorKey = a.doctor_id ?? a.doctorId ?? '';
      const key = `${a.appointment_date}|${a.slot_time}|${doctorKey}`;
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return Object.values(map);
  }, [filteredAppointments]);

  const stats = React.useMemo(() => {
    return {
      total: appointments.length,
      booked: appointments.filter(a => a.status === 'BOOKED').length,
      completed: appointments.filter(a => a.status === 'COMPLETED').length,
      noShow: appointments.filter(a => a.status === 'NO_SHOW').length,
    };
  }, [appointments]);

  const handleReschedule = async () => {
    if (!rescheduleDate || !selectedSlot) return;
    setRescheduling(true);
    try {
      const formData = new URLSearchParams();
      formData.append("appointmentId", selectedAppointment.id);
      formData.append("date", rescheduleDate);
      formData.append("slot", selectedSlot);
      const rescheduleUrl = buildApiUrl("appointments/reschedule");
      console.log("Rescheduling via:", rescheduleUrl);
      const response = await fetch(rescheduleUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Failed to reschedule appointment.");
      }

      setOpenRescheduleModal(false);
      setSlots([]);
      setBookedSlots([]);
      setSelectedSlot("");
      setRescheduleDate("");
      setLastRefreshAt(Date.now());
      await fetchAppointments();
    } catch (err) {
      setError(err.message);
    } finally {
      setRescheduling(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const cancelUrl = buildApiUrl(`appointments/cancel/${selectedAppointment.id}`);
      console.log("Cancelling via:", cancelUrl);
      await fetch(cancelUrl, { method: "POST" });
      setOpenCancelModal(false);
      fetchAppointments();
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const exportCSV = () => {
    if (filteredAppointments.length === 0) return;
    const headers = ["Date", "Time", "Doctor", "Patient", "Phone", "Status"];
    const rows = filteredAppointments.map((a) => [a.appointment_date, a.slot_time, a.doctor_name, a.patient_name, a.phone, a.status]);
    const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `records_export.csv`;
    a.click();
  };

  const getStatusBadge = (status) => {
    const map = { BOOKED: "booked", ARRIVED: "arrived", COMPLETED: "completed", CANCELLED: "cancelled", NO_SHOW: "no_show", CONSULTING: "consulting" };
    return map[status] || "booked";
  };

  return (
    <div className="appt-root">
      <div className="appt-container">
        {/* Header Section */}
        <div className="appt-header">
          <div className="appt-header-left">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h1>Appointments</h1>
              <a href="https://patient.cliniqueue.in" target="_blank" rel="noopener noreferrer" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                border: '1px solid #bfdbfe',
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dbeafe';
                e.currentTarget.style.borderColor = '#93c5fd';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.borderColor = '#bfdbfe';
              }}
              >
                <span>patient.cliniqueue.in</span>
                <ExternalLink size={14} />
              </a>
            </div>
            <p>Operational control room and systemic synchronization dashboard.</p>
          </div>
          <div className="appt-header-actions">
            <button className="appt-btn appt-btn-secondary" onClick={exportCSV}>
              <Download size={14} /> Export Dataset
            </button>
            <button
              className="appt-btn appt-btn-primary"
              title={`Last refreshed ${new Date(lastRefreshAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
              onClick={() => { setLastRefreshAt(Date.now()); fetchAppointments(); }}
            >
              <RefreshCw size={14} /> Sync
            </button>
          </div>
        </div>

        {/* Executive Colorful Stats Cards */}
        {!loading && (
          <div className="appt-stats-grid">
            <div className="appt-stat-card vol">
              <div className="appt-stat-label">Total Volume</div>
              <div className="appt-stat-value">{stats.total}</div>
              <div className="appt-stat-subtext">Active records registry</div>
            </div>
            <div className="appt-stat-card bk">
              <div className="appt-stat-label">Confirmed Bookings</div>
              <div className="appt-stat-value">{stats.booked}</div>
              <div className="appt-stat-subtext">Pending allocation</div>
            </div>
            <div className="appt-stat-card res">
              <div className="appt-stat-label">Resolved Interactions</div>
              <div className="appt-stat-value">{stats.completed}</div>
              <div className="appt-stat-subtext">Successful completions</div>
            </div>
            <div className="appt-stat-card nos">
              <div className="appt-stat-label">Attrition / No-Show</div>
              <div className="appt-stat-value">{stats.noShow}</div>
              <div className="appt-stat-subtext">Unresolved slots</div>
            </div>
          </div>
        )}

        {error && (
          <div className="appt-alert error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Filters Panel */}
        <div className="appt-filters">
          <div className="appt-filters-title">
            <Filter size={14} /> Comprehensive Parameters Control
          </div>

          <div className="appt-mode-toggle">
            {["future", "particular", "range"].map((mode) => (
              <button key={mode} className={`appt-mode-btn ${dateMode === mode ? "active" : ""}`} onClick={() => setDateMode(mode)}>
                {mode === "future" ? "Upcoming Stream" : mode === "particular" ? "Exact Date" : "Custom Scope"}
              </button>
            ))}
          </div>

          <div className="appt-date-inputs">
            {dateMode === "particular" && (
              <div className="appt-input-group">
                <label>Target Date</label>
                <input type="date" className="appt-input" value={particularDate} onChange={(e) => setParticularDate(e.target.value)} />
              </div>
            )}
            {dateMode === "range" && (
              <>
                <div className="appt-input-group">
                  <label>Bound Initiation</label>
                  <input type="date" className="appt-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
                <div className="appt-input-group">
                  <label>Bound Termination</label>
                  <input type="date" className="appt-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              </>
            )}
          </div>

          <div className="appt-filters-row">
            <div className="appt-input-group">
              <label>Filter By Doctor</label>
              <select className="appt-input" value={selectedDoctor} onChange={(e) => setSelectedDoctor(e.target.value)}>
                <option value="">All Doctors</option>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>{d.doctorName || d.doctor_name}</option>
                ))}
              </select>
            </div>
            <div className="appt-input-group">
              <label>Search Patient Identity</label>
              <input type="text" className="appt-input" placeholder="Name or phone number..." value={searchPatient} onChange={(e) => setSearchPatient(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Status Category Tabs */}
        <div className="appt-tabs" style={{ margin: "0 auto 16px auto" }}>
          {["all", "BOOKED", "ARRIVED", "COMPLETED", "NO_SHOW", "CANCELLED"].map((tab) => {
            const count = tab === "all" ? appointments.length : appointments.filter((a) => a.status === tab).length;
            return (
              <button key={tab} className={`appt-tab ${tabValue === tab ? "active" : ""}`} onClick={() => setTabValue(tab)}>
                {tab} ({count})
              </button>
            );
          })}
        </div>

        {/* Data Container Block */}
        {loading ? (
          <div className="appt-table-wrapper">
            <div className="appt-loading">
              <Loader size={18} className="appt-spinner" />
              <span>Parsing appointment datasets...</span>
            </div>
          </div>
        ) : filteredAppointments.length > 0 ? (
          <div className="appt-table-wrapper">
            {windowWidth > 768 ? (
              <table className="appt-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Phone</th>
                    <th>Doctor</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAppointments.map((appt, idx) => (
                    <tr key={appt.id}>
                      <td data-label="Patient">{appt.patient_name}</td>
                      <td data-label="Phone">{appt.phone}</td>
                      <td data-label="Doctor">{appt.doctor_name}</td>
                      <td data-label="Date">{appt.appointment_date}</td>
                      <td data-label="Time">{appt.slot_time}</td>
                      <td data-label="Status"><span className={`appt-badge ${getStatusBadge(appt.status)}`}>{appt.status}</span></td>
                      <td data-label="Actions">
                        <div className="appt-actions">
                          <button className="appt-icon-btn" onClick={() => { setSelectedAppointment(appt); setOpenViewModal(true); }}>
                            <Eye size={14} />
                          </button>
                          {appt.status === "BOOKED" && (
                            <>
                              <button className="appt-icon-btn" onClick={() => { setSelectedAppointment(appt); setRescheduleDate(""); setSelectedSlot(""); setOpenRescheduleModal(true); }}>
                                <Edit2 size={14} />
                              </button>
                              <button className="appt-icon-btn danger" onClick={() => { setSelectedAppointment(appt); setOpenCancelModal(true); }}>
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="appt-card-list">
                {groupedAppointments.map((group) => (
                  <div key={`${group[0]?.id}-${group[0]?.slot_time}`} className="appt-card-group">
                    <div className="appt-card-header">
                      <div>
                        <div className="appt-card-group-title">{group[0]?.appointment_date} — {group[0]?.slot_time}</div>
                        <div className="appt-card-group-sub">{group[0]?.doctor_name}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, color: 'var(--text-dim)', fontWeight: 700 }}>{group.length} slot{group.length>1 ? 's' : ''}</div>
                      </div>
                    </div>

                    {group.map((appt, idx) => (
                      <div key={appt.id} className="appt-card-row">
                        <div className="appt-card-left">
                          <div className="appt-avatar">{appt.patient_name?.charAt(0) || 'P'}</div>
                          <div className="appt-card-meta">
                            <div className="appt-patient-name">{appt.patient_name}</div>
                            <div className="appt-patient-phone">{appt.phone}</div>
                          </div>
                        </div>

                        <div className="appt-card-details">
                          <div className="appt-card-doctor-name">{appt.doctor_name}</div>
                          <div className="appt-card-datetime">{appt.appointment_date} • {appt.slot_time}</div>
                        </div>

                        <div className="appt-card-meta-actions">
                          <div>
                            <span className={`appt-badge ${getStatusBadge(appt.status)}`}>{appt.status}</span>
                          </div>
                          <div className="appt-actions">
                            <button className="appt-icon-btn" onClick={() => { setSelectedAppointment(appt); setOpenViewModal(true); }}>
                              <Eye size={14} />
                            </button>
                            {idx === 0 && appt.status === "BOOKED" && (
                              <>
                                <button className="appt-icon-btn" onClick={() => { setSelectedAppointment(appt); setRescheduleDate(""); setSelectedSlot(""); setOpenRescheduleModal(true); }}>
                                  <Edit2 size={14} />
                                </button>
                                <button className="appt-icon-btn danger" onClick={() => { setSelectedAppointment(appt); setOpenCancelModal(true); }}>
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="appt-empty-container">
            <h3 className="appt-empty-headline">No Appointments Found</h3>
            <p className="appt-empty-subline">No active data matches this filter criteria view parameters.</p>
          </div>
        )}
      </div>

      {/* Details Dialog Modal */}
      {openViewModal && (
        <div className="appt-modal-overlay" onClick={() => setOpenViewModal(false)}>
          <div className="appt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="appt-modal-header">
              <div className="appt-modal-title">Appointment Summary</div>
              <button className="appt-modal-close" onClick={() => setOpenViewModal(false)}><X size={14} /></button>
            </div>
            <div className="appt-modal-body">
              {selectedAppointment && (
                <>
                  <div className="appt-modal-field">
                    <div className="appt-modal-label">Assigned Doctor</div>
                    <div className="appt-modal-value">{selectedAppointment.doctor_name}</div>
                  </div>
                  <div className="appt-modal-field">
                    <div className="appt-modal-label">Patient Details</div>
                    <div className="appt-modal-value">{selectedAppointment.patient_name} — {selectedAppointment.phone}</div>
                  </div>
                  <div className="appt-modal-field">
                    <div className="appt-modal-label">Appointment Schedule</div>
                    <div className="appt-modal-value">{selectedAppointment.appointment_date} @ {selectedAppointment.slot_time}</div>
                  </div>
                </>
              )}
            </div>
            <div className="appt-modal-footer">
              <button className="appt-btn appt-btn-secondary" onClick={() => setOpenViewModal(false)}>Close View</button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule architecture Modal */}
      {openRescheduleModal && (
        <div className="appt-modal-overlay" onClick={() => setOpenRescheduleModal(false)}>
          <div className="appt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="appt-modal-header">
              <div className="appt-modal-title">Reschedule Appointment</div>
              <button className="appt-modal-close" onClick={() => setOpenRescheduleModal(false)}><X size={14} /></button>
            </div>
            <div className="appt-modal-body">
              {selectedAppointment && (
                <>
                  <div className="appt-modal-field">
                    <label className="appt-modal-label">Select New Date</label>
                    <input type="date" className="appt-input" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} min={getTodayIST()} />
                  </div>
                  {fetchingSlots && <div className="appt-loading"><Loader size={14} className="appt-spinner" /><span>Fetching calendar slots...</span></div>}
                  {rescheduleDate && !fetchingSlots && (
                    <div className="appt-modal-field">
                      <label className="appt-modal-label">Available Time Slots</label>
                      <select className="appt-input" value={selectedSlot} onChange={(e) => setSelectedSlot(e.target.value)}>
                        <option value="">Choose a slot...</option>
                        {slots.map((slot) => (
                          <option key={slot} value={slot} disabled={bookedSlots.includes(slot)}>
                            {slot} {bookedSlots.includes(slot) ? "— [Booked]" : "— [Available]"}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="appt-modal-footer">
              <button className="appt-btn appt-btn-secondary" onClick={() => setOpenRescheduleModal(false)}>Cancel</button>
              <button className="appt-btn appt-btn-primary" onClick={handleReschedule} disabled={rescheduling || !rescheduleDate || !selectedSlot}>
                {rescheduling ? "Updating..." : "Confirm Change"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Warning Lightbox */}
      {openCancelModal && (
        <div className="appt-modal-overlay" onClick={() => setOpenCancelModal(false)}>
          <div className="appt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="appt-modal-header">
              <div className="appt-modal-title">Cancel Appointment Confirmation</div>
              <button className="appt-modal-close" onClick={() => setOpenCancelModal(false)}><X size={14} /></button>
            </div>
            <div className="appt-modal-body">
              <div className="appt-modal-field">
                <div className="appt-alert error" style={{ margin: 0 }}>
                  <AlertCircle size={16} />
                  <span>Are you sure you want to cancel this appointment slot? Releasing this window re-opens booking availability.</span>
                </div>
              </div>
            </div>
            <div className="appt-modal-footer">
              <button className="appt-btn appt-btn-secondary" onClick={() => setOpenCancelModal(false)}>Keep Appointment</button>
              <button className="appt-btn appt-btn-primary" style={{ background: "var(--semantic-danger)", boxShadow: "none" }} onClick={handleCancel} disabled={cancelling}>
                {cancelling ? "Processing..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AppointmentsPremium;